import { useMemo } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { NUMERIC_TO_ALPHA } from "@/lib/countryColors";
import { isArab, bboxOf } from "@/lib/mapGeometry";
import { VIEW_W, VIEW_H } from "@/components/map/MapParts";

// Small countries whose shape is barely visible on-screen — always show a callout flag badge.
const SMALL_COUNTRY_CODES = new Set(["BH", "QA", "KW", "DJ", "KM", "PS"]);
// Bahrain and Qatar are ~6px apart at this scale: nudge their pins so both stay clickable.
const PIN_OFFSET = { BH: [-9, -9], QA: [9, 9] };

// Center + scale calibrated to fit all 23 countries: Morocco/EH → Oman, Syria → Comoros.
export const makeProjection = () => geoMercator().center([22, 14]).scale(520).translate([VIEW_W / 2, VIEW_H / 2 + 20]);

/** Projection, Arab shapes (with flag bbox), background outlines, disputed overlay and micro-state pins. */
export function useAtlasGeometry(features, countries) {
  const codeToCountry = useMemo(() => Object.fromEntries(countries.map((c) => [c.code, c])), [countries]);

  const geo = useMemo(() => {
    if (!features) return { pathGen: null, arabShapes: [], nonArabFeatures: [], pins: [], project: null };
    const proj = makeProjection();
    const gen = geoPath(proj);
    const arabShapes = features.filter(isArab).map((f) => ({ code: NUMERIC_TO_ALPHA[f.properties.id], d: gen(f), bbox: bboxOf(gen, f) }));
    const pins = countries.filter((c) => SMALL_COUNTRY_CODES.has(c.code)).map((c) => {
      const [x, y] = proj([c.position.lng, c.position.lat]);
      const [dx, dy] = PIN_OFFSET[c.code] || [0, 0];
      return { ...c, x: x + dx, y: y + dy };
    });
    return { pathGen: gen, arabShapes, nonArabFeatures: features.filter((f) => !isArab(f)), pins, project: (lng, lat) => proj([lng, lat]) };
  }, [features, countries]);

  const disputedShapes = useMemo(() => geo.arabShapes.filter((s) => s.code === "EH"), [geo.arabShapes]);

  return { ...geo, codeToCountry, disputedShapes };
}
